import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import {
  Link,
  Container,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Paper,
  Fade,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useResponsive from '../hooks/useResponsive';
import logoImage from '../media/Techveel-newlogo.jpg';
import Iconify from '../components/iconify';
import axios from '../axios';

// Premium admin background image - modern gradient abstract
const loginBanner = 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1200&q=80';

const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    width: '55%',
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    background: `linear-gradient(135deg, rgba(10, 20, 40, 0.85) 0%, rgba(25, 50, 80, 0.75) 50%, rgba(10, 30, 60, 0.85) 100%), url("${loginBanner}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: theme.spacing(6),
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-20%',
      width: '600px',
      height: '600px',
      background: 'radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, transparent 70%)',
      borderRadius: '50%',
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-30%',
      left: '-10%',
      width: '500px',
      height: '500px',
      background: 'radial-gradient(circle, rgba(0, 230, 118, 0.08) 0%, transparent 70%)',
      borderRadius: '50%',
      zIndex: 1,
    },
  },
}));

const StyledSectionContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  maxWidth: 440,
  width: '100%',
  color: '#fff',
  textAlign: 'left',
}));

const StyledContent = styled(Paper)(({ theme }) => ({
  maxWidth: 440,
  width: '100%',
  margin: 'auto',
  padding: theme.spacing(5, 4),
  borderRadius: theme.spacing(3),
  boxShadow: '0 24px 80px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.06)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 2.5),
    borderRadius: theme.spacing(2.5),
    margin: theme.spacing(2),
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.08)',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    padding: theme.spacing(4.5, 3.5),
    maxWidth: 400,
  },
  [theme.breakpoints.between('md', 'lg')]: {
    padding: theme.spacing(5, 4),
    maxWidth: 420,
  },
}));

const StyledLogoWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    maxWidth: 200,
    height: 'auto',
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.05))',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 160,
    },
    [theme.breakpoints.between('sm', 'md')]: {
      maxWidth: 180,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.neutral,
    transition: 'all 0.25s ease',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}15`,
      borderColor: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
}));

const StyledLoginButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  fontWeight: 600,
  fontSize: '0.9375rem',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: '0 8px 28px rgba(25, 118, 210, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.4)',
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
  },
  '&.Mui-disabled': {
    background: theme.palette.grey[300],
  },
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& .MuiAlert-icon': {
    fontSize: '1.2rem',
  },
  '& .MuiAlert-message': {
    fontSize: '0.875rem',
    padding: '6px 0',
    fontWeight: 500,
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    borderColor: theme.palette.divider,
  },
}));

export default function LoginPage() {
  const lgUp = useResponsive('up', 'lg');
  const navigate = useNavigate();
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [UserName, setUserName] = useState('');
  const [UserPassword, setUserPassword] = useState('');
  const [error, setError] = useState('');
  const [errorColor, setErrorColor] = useState('');
  const [loading, setLoading] = useState(false);

  const tokent =
    'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ';

  const handleClick = async () => {
    if (!UserName) {
      setError('Please enter your username');
      setErrorColor('error');
      setTimeout(() => {
        setError('');
        setErrorColor('');
      }, 3000);
      return;
    }

    if (!UserPassword) {
      setError('Please enter your password');
      setErrorColor('error');
      setTimeout(() => {
        setError('');
        setErrorColor('');
      }, 3000);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.instance.post(
        '/UserLogin',
        { UserName, UserPassword },
        {
          headers: { Authorization: tokent, 'Content-Type': 'application/json' },
        }
      );

      console.log('response data', response.data[0]);

      if (response.data[0]?.ActiveStatus === 'Y') {
        setError('Login successful! Redirecting...');
        setErrorColor('success');

        localStorage.setItem('LoginStatus', response.data[0].ActiveStatus);
        localStorage.setItem('User_EMail', response.data[0].Email);
        localStorage.setItem('User_Name', response.data[0].UserName);

        setTimeout(() => {
          navigate('/dashboard', { replace: true });
          setError('');
          setErrorColor('');
          setLoading(false);
        }, 1500);
      } else {
        setError('Invalid login credentials. Please try again.');
        setErrorColor('error');
        setLoading(false);
        setTimeout(() => {
          setError('');
          setErrorColor('');
        }, 3000);
      }
    } catch (error) {
      setError('Invalid login credentials. Please try again.');
      setErrorColor('error');
      setLoading(false);
      setTimeout(() => {
        setError('');
        setErrorColor('');
      }, 3000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleClick();
    }
  };

  return (
    <StyledRoot>
      {lgUp && (
        <StyledSection>
          <StyledSectionContent>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 1,
                }}
              >
                Welcome to ASK Dashboard
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '0 4px 24px rgba(0,0,0,0.2)',
                  letterSpacing: '-0.02em',
                  fontSize: { lg: '3.2rem', xl: '3.8rem' },
                  lineHeight: 1.2,
                }}
              >
                Admin Dashboard
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.85,
                  fontWeight: 400,
                  textShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  maxWidth: 380,
                  lineHeight: 1.7,
                  fontSize: '1.1rem',
                }}
              >
                Secure access to manage your website, content, and analytics in one powerful platform.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
              <Stack spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Iconify icon="mdi:shield-check" width={24} height={24} />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  Enterprise Security
                </Typography>
              </Stack>
              <Stack spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Iconify icon="mdi:chart-line" width={24} height={24} />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  Real-time Analytics
                </Typography>
              </Stack>
              <Stack spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Iconify icon="mdi:cloud-check" width={24} height={24} />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  Cloud Management
                </Typography>
              </Stack>
            </Box>
          </StyledSectionContent>
        </StyledSection>
      )}

      <Container
        maxWidth={false}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1.5, sm: 2, md: 3 },
          flex: 1,
        }}
      >
        <Fade in timeout={600}>
          <StyledContent elevation={0}>
            <StyledLogoWrapper>
              <img src={logoImage} alt="Techveel" />
            </StyledLogoWrapper>

            <Typography
              variant="h4"
              textAlign="center"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                color: 'text.secondary',
                mb: 3,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              Sign in to access your admin dashboard
            </Typography>

            {error && (
              <StyledAlert
                severity={errorColor === 'success' ? 'success' : 'error'}
                icon={
                  errorColor === 'success' ? <Iconify icon="mdi:check-circle" /> : <Iconify icon="mdi:alert-circle" />
                }
                onClose={() => {
                  setError('');
                  setErrorColor('');
                }}
              >
                {error}
              </StyledAlert>
            )}

            <Stack spacing={2.5}>
              <StyledTextField
                name="UserName"
                label="Username"
                placeholder="Enter your username"
                value={UserName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyPress={handleKeyPress}
                fullWidth
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="mdi:account" width={20} height={20} sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <StyledTextField
                name="UserPassword"
                label="Password"
                placeholder="Enter your password"
                type={showUserPassword ? 'text' : 'password'}
                value={UserPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                fullWidth
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="mdi:lock" width={20} height={20} sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowUserPassword(!showUserPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: 'text.secondary' }}
                      >
                        <Iconify icon={showUserPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={20} height={20} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* <Box
              sx={{
                mt: 1.5,
                mb: 3,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Link
                href="#"
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textDecoration: 'none',
                  fontSize: '0.8125rem',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot password?
              </Link>
            </Box> */}

            <StyledLoginButton
              fullWidth
              sx={{
                mt: 1.5,
              }}
              size="large"
              type="submit"
              variant="contained"
              onClick={handleClick}
              loading={loading}
              loadingIndicator={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={18} color="inherit" />
                  <Typography variant="body2">Signing in...</Typography>
                </Stack>
              }
            >
              Sign In
            </StyledLoginButton>

            <StyledDivider>
              <Typography variant="caption" sx={{ color: 'text.disabled', px: 1 }}>
                Secure Admin Access
              </Typography>
            </StyledDivider>

            <Typography
              variant="caption"
              textAlign="center"
              sx={{
                display: 'block',
                color: 'text.disabled',
                letterSpacing: '0.02em',
                fontSize: '0.6875rem',
              }}
            >
              &copy; {new Date().getFullYear()} Techveel. All rights reserved.
            </Typography>
          </StyledContent>
        </Fade>
      </Container>
    </StyledRoot>
  );
}
